import React, { useMemo } from "react";

import { orderBy } from "lodash";

import { Proposal } from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import { TLHSwapAccepted } from "@fartherfinance/frontend/api/Investment/types";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";

import { toSortable } from "@src/multiCustodian/components/Client/Accounts/Holdings/Components/BreakdownTable/HoldingsTable";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";
import { Cell, Row } from "@src/sharedComponents/Table/Types";

import { ESAndListLookup } from "./createEquivalentSecurityAndEsListLookup";
import SecurityCell from "./SecurityCell";

type SecurityDb = Awaited<
  ReturnType<typeof useSecuritiesBySecurityIds>
>["data"];

type TradeColumns =
  | "Trade No"
  | "Account"
  | "Ticker"
  | "Side"
  | "Quantity"
  | "Price"
  | "Market Value";

interface Props {
  proposal: Proposal | undefined;
  securityDb: SecurityDb | undefined;
  excludedAssets?: Array<{ securityId: string; createdAt?: Date }>;
  acceptedTLHSwaps: TLHSwapAccepted[];
  esLookup?: ESAndListLookup;
}

const TradeList: React.FC<Props> = (props) => {
  const trades = useMemo((): Row<TradeColumns, Cell>[] | undefined => {
    const secDb = props.securityDb;

    // Edge-Case: If we have no trades then secDB is undefined and we return an empty array
    if (props.proposal !== undefined && props.proposal.trades.length === 0) {
      return [];
    }

    if (props.proposal === undefined || secDb === undefined) {
      return undefined;
    }

    const orderedTrades = orderBy(
      props.proposal.trades,
      [
        (t) =>
          secDb
            .find((x) => x.id === t.securityId)
            ?.identifiers.find((x) => x.registrar === "TICKER")?.identifier ??
          "ZZZZ", // put it at the end if no ticker found,
        (t) => t.custodianAccountId,
      ],
      ["asc", "asc"]
    );

    return orderedTrades.map((trade, idx): Row<TradeColumns, Cell> => {
      const esListMatch =
        props.esLookup !== undefined
          ? props.esLookup.lists[props.esLookup.securities[trade.securityId]]
          : undefined;

      const esPillData =
        esListMatch !== undefined && esListMatch.matches.length >= 2
          ? {
              listIndex: esListMatch.listIndex,
              listName: esListMatch.listName,
            }
          : undefined;

      return {
        key: `${trade.physicalAccountId}-${trade.securityId}`,
        "Trade No": {
          value: `Trade ${idx + 1}`,
          fullValue: toSortable(idx),
        },
        Account: trade.custodianAccountId,
        Ticker: {
          value: (
            <SecurityCell
              securityId={trade.securityId}
              securityDb={props.securityDb}
              isDoNotSell={props.excludedAssets?.some(
                (asset) => asset.securityId === trade.securityId
              )}
              isTaxLossHarvest={
                props.acceptedTLHSwaps.some(
                  (swap) =>
                    swap.originalSecurity === trade.securityId ||
                    swap.replacementSecurity === trade.securityId
                ) ?? false
              }
              equivalentSecurityPillData={esPillData}
            />
          ),
          fullValue:
            props.securityDb
              ?.find((x) => x.id === trade.securityId)
              ?.identifiers.find((x) => x.registrar === "TICKER")?.identifier ??
            "ZZZZ",
        },
        Side: trade.side.toUpperCase(),
        Quantity: {
          value: trade.quantity.toNumber().toLocaleString("en-US"),
          fullValue: toSortable(trade.quantity.toNumber()),
        },
        Price: {
          value: trade.price.toNumber().toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          fullValue: toSortable(trade.price.toNumber(), 2),
        },
        "Market Value": trade.quantity
          .mul(trade.price)
          .toNumber()
          .toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
      };
    });
  }, [
    props.excludedAssets,
    props.proposal,
    props.securityDb,
    props.acceptedTLHSwaps,
    props.esLookup,
  ]);

  if (trades === undefined) {
    return (
      <FullDataTable<TradeColumns, Cell>
        key={"trades-loading"}
        useQueryParams={false}
        columns={[
          "Trade No",
          "Account",
          "Ticker",
          "Side",
          "Quantity",
          "Price",
          "Market Value",
        ]}
        defaultSortColumn={["Trade No", "asc"]}
        rows={Array.from({ length: 10 }).map((_, idx) => ({
          key: idx.toString(),
          "Trade No": { value: <Skeleton width={100} />, fullValue: "" },
          Account: { value: <Skeleton width={100} />, fullValue: "" },
          Ticker: { value: <Skeleton width={100} />, fullValue: "" },
          Side: { value: <Skeleton width={100} />, fullValue: "" },
          Quantity: { value: <Skeleton width={100} />, fullValue: "" },
          Price: { value: <Skeleton width={100} />, fullValue: "" },
          "Market Value": { value: <Skeleton width={100} />, fullValue: "" },
        }))}
        emptyCell={<Skeleton />}
        hidePagination
        defaultRowsPerPage={10}
      />
    );
  }

  if (trades.length === 0) {
    return (
      <AlertInline
        severity="warning"
        message="There are no trades in this trade proposal"
      />
    );
  }

  return (
    <FullDataTable<TradeColumns, Cell>
      key={"trades-loaded"}
      useQueryParams={false}
      columns={[
        "Trade No",
        "Account",
        "Ticker",
        "Side",
        "Quantity",
        "Price",
        "Market Value",
      ]}
      defaultSortColumn={["Trade No", "asc"]}
      rows={trades}
      emptyCell={<Skeleton />}
      hidePagination
      defaultRowsPerPage={trades.length}
    />
  );
};

export default TradeList;
